"use client";

import React from "react";
import { twMerge } from "tailwind-merge";
import { BreadcrumbItem, type BreadcrumbItemProps, Breadcrumbs } from "../Breadcrumbs";
import { Heading } from "../Heading";
import { Section, type SectionProps } from "../Section";
import { SectionNav as SectionNavComponent, type SectionNavItem, type SectionNavOrientation } from "../SectionNav";
import { Skeleton } from "../Skeleton";
import { Tabs as RACTabs, Tab, TabList } from "../Tabs";

/**
 * PageLayout — opinionated, SSR-friendly page scaffold for dashboards.
 *
 * Structure:
 *   <PageLayout>
 *     <PageLayout.Header
 *       title="..."
 *       subtitle="..."
 *       breadcrumbs={[...]}
 *       media={<div>images/maps</div>}
 *       actions={[<Button/>, <Button/>]}
 *     />
 *     <PageLayout.Tabs tabs={[...]} value=... onChange=... />
 *     <PageLayout.Content>
 *       <Grid cols={{ base: 1, lg: 4 }} gap="lg">
 *         ...cards/tables/content...
 *       </Grid>
 *     </PageLayout.Content>
 *   </PageLayout>
 *
 * With Section Navigation:
 *   <PageLayout>
 *     <PageLayout.Header title="..." />
 *     <PageLayout.Content withSectionNav="left">
 *       <Section id="overview" navLabel="Overview" title="Overview">...</Section>
 *       <Section id="details" navLabel="Details" title="Details">...</Section>
 *     </PageLayout.Content>
 *   </PageLayout>
 *
 * With Sticky Header and Scrollable Content (data-heavy pages):
 *   <PageLayout className="flex flex-col min-h-0">
 *     <PageLayout.Header title="..." sticky={true} stickyTop="0" />
 *     <DataControls sticky={true} stickyTop="120px" />
 *     <PageLayout.ScrollableContent>
 *       <DataTable data={data} columns={columns} />
 *     </PageLayout.ScrollableContent>
 *   </PageLayout>
 *
 * Notes:
 * - Router-agnostic: Breadcrumbs accept either structured items with a Link component, or a custom ReactNode.
 * - Accessible: Header uses h1; Tabs are roving-`tablist`.
 * - Composable: Use with Grid, Section, Card, DataControls and other components for flexible layouts.
 * - Media support: Header can include hero images, maps, or other visual content.
 * - Actions: Right-aligned on desktop, below subtitle on mobile.
 * - Section nav: Automatically extracts navigation from Section children when withSectionNav is enabled.
 * - Sticky header support: Make headers sticky for data-heavy pages with scrollable content.
 * - ScrollableContent helper: Ensures proper overflow handling with sticky elements.
 * - Works with sticky DataControls: Configurable z-index and offset for complex layouts.
 * - Styling: token-oriented utility classes (match to your Tailwind preset/tokens).
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type PageBreadcrumbItem = BreadcrumbItemProps & { label: string };

export type PageLayoutProps = {
  children: React.ReactNode;
  /** Constrain width and apply default page rhythm */
  maxWidth?: number; // px
  paddingXClass?: string; // e.g., "px-4 md:px-6"
  paddingYClass?: string; // e.g., "py-4 md:py-6"
  className?: string;
};

export function PageLayout({
  children,
  maxWidth = 1400,
  paddingXClass = "px-4 md:px-6",
  paddingYClass = "pt-8 pb-14 md:pt-12 md:pb-28",
  className,
}: PageLayoutProps) {
  return (
    <div
      className={twMerge(`w-full ${paddingXClass} ${paddingYClass}`, className)}
      style={{ marginInline: "auto", maxWidth }}
    >
      {children}
    </div>
  );
}

// ——————————————————————————————————————————
// Header
// ——————————————————————————————————————————

export type PageHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  breadcrumbs?: PageBreadcrumbItem[];
  /** Custom breadcrumbs node if you don't want the built-in renderer */
  breadcrumbsNode?: React.ReactNode;
  /** Optional media content (images, maps, etc.) rendered after title/subtitle */
  media?: React.ReactNode;
  /** Optional actions (buttons, filters) - right-aligned on desktop, below subtitle on mobile. Array ensures consistent gap spacing. */
  actions?: React.ReactNode[];
  /** Optional metadata bar shown below actions with top border. Renders as-is for full control. */
  metadata?: React.ReactNode;
  /** Optional slot to replace the default heading element */
  headingAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /** Make header sticky at top of viewport */
  sticky?: boolean;
  /** Sticky top offset in pixels or CSS value (default: "0") */
  stickyTop?: string;
  /** Z-index for sticky positioning (default: 10) */
  zIndex?: number;
  /** Show skeleton loaders based on provided props */
  isLoading?: boolean;
  className?: string;
};

export function Header({
  title,
  subtitle,
  breadcrumbs,
  breadcrumbsNode,
  media,
  actions,
  metadata,
  headingAs,
  sticky,
  stickyTop = "0",
  zIndex = 10,
  isLoading = false,
  className,
}: PageHeaderProps) {
  const stickyClasses = sticky ? "sticky bg-background-body" : "";
  const stickyStyles = sticky ? { top: stickyTop, zIndex } : {};

  // Loading state - render skeletons based on provided props
  if (isLoading) {
    return (
      <header
        className={twMerge("mb-8 md:mb-12", stickyClasses, className)}
        style={stickyStyles}
        aria-labelledby="page-title"
      >
        {/* Breadcrumbs skeleton */}
        {breadcrumbsNode || breadcrumbs?.length ? (
          <div className="mb-2 md:mb-4">
            <Skeleton className="h-4 w-32" />
          </div>
        ) : null}

        {/* Title, Subtitle, Actions skeleton */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0 flex-1">
            {/* Title skeleton */}
            <div id="page-title">
              <Skeleton className="h-8 w-64" />
            </div>

            {/* Subtitle skeleton */}
            {subtitle ? <Skeleton className="mt-2 h-5 w-48" /> : null}

            {/* Metadata skeleton */}
            {metadata ? <Skeleton className="mt-2 h-4 w-40" /> : null}

            {/* Actions skeleton on mobile */}
            {actions?.length ? (
              <div className="mt-6 flex flex-col items-stretch gap-3 md:hidden">
                {actions.map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            ) : null}
          </div>

          {/* Actions skeleton on desktop */}
          {actions?.length ? (
            <div className="hidden md:flex md:items-start gap-2 shrink-0">
              {actions.map((_, i) => (
                <Skeleton key={i} className="h-9 w-24" />
              ))}
            </div>
          ) : null}
        </div>

        {/* Media skeleton */}
        {media ? (
          <div className="mt-4 md:mt-6">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : null}
      </header>
    );
  }

  // Regular loaded state
  return (
    <header
      className={twMerge("mb-8 md:mb-12", stickyClasses, className)}
      style={stickyStyles}
      aria-labelledby="page-title"
    >
      {/* Breadcrumbs */}
      {breadcrumbsNode ? (
        <div className="mb-2 md:mb-4">{breadcrumbsNode}</div>
      ) : breadcrumbs?.length ? (
        <div className="mb-2 md:mb-4">
          <Breadcrumbs>
            {breadcrumbs.map((bc, i) => {
              const { label, ...rest } = bc;
              return (
                <BreadcrumbItem key={`${label}-${i}`} {...rest}>
                  {label}
                </BreadcrumbItem>
              );
            })}
          </Breadcrumbs>
        </div>
      ) : null}

      {/* Title, Subtitle, and Actions */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div id="page-title">
            <Heading tag={headingAs || "h1"} size="xl">
              {title}
            </Heading>
          </div>
          {subtitle ? <p className="mt-2 text-base md:text-lg text-text-secondary">{subtitle}</p> : null}

          {/* Metadata: below subtitle on both mobile and desktop */}
          {metadata ? <div className="mt-2 text-sm text-text-secondary">{metadata}</div> : null}

          {/* Actions on mobile: below metadata */}
          {actions?.length ? (
            <div className="mt-6 flex flex-col items-stretch gap-3 md:hidden">
              {actions.map((action, i) => (
                <React.Fragment key={i}>{action}</React.Fragment>
              ))}
            </div>
          ) : null}
        </div>

        {/* Actions on desktop: right-aligned and top-aligned with heading */}
        {actions?.length ? (
          <div className="hidden md:flex md:items-start gap-2 shrink-0">
            {actions.map((action, i) => (
              <React.Fragment key={i}>{action}</React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      {/* Media content (images, maps, etc.) */}
      {media ? <div className="mt-4 md:mt-6">{media}</div> : null}
    </header>
  );
}

// ——————————————————————————————————————————
// Tabs (wrapper around React Aria Tabs)
// ——————————————————————————————————————————

type PageLayoutTab = {
  id: string;
  label: React.ReactNode;
  disabled?: boolean;
};
export type TabsProps = {
  tabs: PageLayoutTab[];
  value?: string; // controlled
  defaultValue?: string; // uncontrolled
  onChange?: (id: string) => void;
  className?: string;
  children?: React.ReactNode; // Optional: for rendering TabPanels
};

export function Tabs({ tabs, value, defaultValue, onChange, className, children }: TabsProps) {
  // Convert onChange to handle Key type from React Aria
  const handleSelectionChange = (key: React.Key) => {
    onChange?.(key as string);
  };

  return (
    <div className={twMerge("mb-3 md:mb-4 border-b-2 border-border-muted", className)}>
      <RACTabs
        selectedKey={value}
        defaultSelectedKey={defaultValue || tabs[0]?.id}
        onSelectionChange={handleSelectionChange}
      >
        <TabList className="after:bg-transparent">
          {tabs.map((t) => (
            <Tab key={t.id} id={t.id} isDisabled={t.disabled}>
              {t.label}
            </Tab>
          ))}
        </TabList>
        {children}
      </RACTabs>
    </div>
  );
}

// ——————————————————————————————————————————
// Content & Aside
// ——————————————————————————————————————————

export type PageContentProps = {
  children: React.ReactNode;
  className?: string;
  /** Enable section navigation. Position: 'above' (horizontal), 'left' (vertical), 'right' (vertical) */
  withSectionNav?: "above" | "left" | "right";
  /** Custom nav width when positioned left/right (default: 240px) */
  navWidth?: string;
  /** Gap between nav and content when positioned left/right (default: 2rem) */
  gap?: string;
  /** Active section ID (controlled mode) */
  activeSection?: string | null;
  /** Callback when active section changes */
  onSectionChange?: (sectionId: string) => void;
  /** Custom scroll behavior */
  scrollBehavior?: ScrollBehavior;
  /** ID prefix for section elements (default: 'section-') */
  sectionIdPrefix?: string;
  /** ID of the scrollable container element for scroll-to and scroll tracking */
  scrollContainerId?: string;
  /** Optional heading above the vertical section nav (e.g. "On this page"). */
  navHeading?: string;
  /** Sticky top offset for the vertical rail (default keeps the built-in top-20). */
  navStickyTop?: string;
};

export function Content({
  children,
  className,
  withSectionNav,
  navWidth = "240px",
  gap = "2rem",
  activeSection,
  onSectionChange,
  scrollBehavior,
  sectionIdPrefix = "section-",
  scrollContainerId,
  navHeading,
  navStickyTop,
}: PageContentProps) {
  // If withSectionNav is enabled, use SectionNavWithLayout
  if (withSectionNav) {
    return (
      <SectionNavWithLayout
        position={withSectionNav}
        navWidth={navWidth}
        gap={gap}
        activeSection={activeSection}
        onSectionChange={onSectionChange}
        scrollBehavior={scrollBehavior}
        sectionIdPrefix={sectionIdPrefix}
        scrollContainerId={scrollContainerId}
        navHeading={navHeading}
        navStickyTop={navStickyTop}
        className={className}
      >
        {children}
      </SectionNavWithLayout>
    );
  }

  return <div className={twMerge("w-full space-y-8 md:space-y-12", className)}>{children}</div>;
}

export type PageAsideProps = {
  children: React.ReactNode;
  /** Sticky aside on wide screens */
  sticky?: boolean;
  className?: string;
};

export function Aside({ children, sticky = true, className }: PageAsideProps) {
  return <aside className={twMerge(sticky ? "lg:sticky lg:top-20" : "", className)}>{children}</aside>;
}

// ——————————————————————————————————————————
// Internal: SectionNav with Layout
// ——————————————————————————————————————————

type SectionNavWithLayoutProps = {
  activeSection?: string | null;
  onSectionChange?: (sectionId: string) => void;
  position: "above" | "left" | "right";
  navWidth: string;
  gap: string;
  children: React.ReactNode;
  className?: string;
  scrollBehavior?: ScrollBehavior;
  sectionIdPrefix: string;
  scrollContainerId?: string;
  navHeading?: string;
  navStickyTop?: string;
};

/**
 * Internal component used by PageLayout.Content when withSectionNav is enabled.
 * Handles section extraction, ID prefixing, and layout rendering.
 */
function SectionNavWithLayout({
  activeSection,
  onSectionChange,
  position,
  navWidth,
  gap,
  children,
  className,
  scrollBehavior,
  sectionIdPrefix,
  scrollContainerId,
  navHeading,
  navStickyTop,
}: SectionNavWithLayoutProps) {
  // Extract sections from Section children
  const sections = React.useMemo(() => {
    const extractedSections: SectionNavItem[] = [];

    React.Children.forEach(children, (child) => {
      // Use displayName for more robust checking (works with memo/forwardRef)
      if (
        React.isValidElement(child) &&
        (child.type === Section ||
          (typeof child.type === "object" && (child.type as { displayName?: string })?.displayName === "Section") ||
          (typeof child.type === "function" && (child.type as { displayName?: string })?.displayName === "Section"))
      ) {
        const props = child.props as SectionProps;

        if (props.id) {
          extractedSections.push({
            id: props.id,
            label: props.navLabel || (typeof props.title === "string" ? props.title : `Section ${props.id}`),
          });
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Section component used within PageLayout.Content with withSectionNav must have an id prop");
        }
      }
    });

    return extractedSections;
  }, [children]);

  // Render children with proper section IDs and spacing
  const contentWithSpacing = React.useMemo(() => {
    // Clone Section children and ensure they have the proper ID with prefix
    const processedChildren = React.Children.map(children, (child) => {
      // Use same robust type checking as section extraction
      if (
        React.isValidElement(child) &&
        (child.type === Section ||
          (typeof child.type === "object" && (child.type as { displayName?: string })?.displayName === "Section") ||
          (typeof child.type === "function" && (child.type as { displayName?: string })?.displayName === "Section"))
      ) {
        const props = child.props as SectionProps;

        if (props.id) {
          return React.cloneElement(child, {
            ...props,
            id: `${sectionIdPrefix}${props.id}`,
          } as SectionProps);
        }
      }
      return child;
    });

    // Wrap in consistent spacing container
    return <div className="space-y-8 md:space-y-12">{processedChildren}</div>;
  }, [children, sectionIdPrefix]);

  // Reusable nav component renderer
  const renderNav = (orientation: SectionNavOrientation, navClassName?: string) => (
    <SectionNavComponent
      sections={sections}
      activeSection={activeSection}
      onSectionChange={onSectionChange}
      orientation={orientation}
      scrollBehavior={scrollBehavior}
      sectionIdPrefix={sectionIdPrefix}
      scrollContainerId={scrollContainerId}
      heading={navHeading}
      className={navClassName}
    />
  );

  // For position="above", render nav above content
  if (position === "above") {
    return (
      <div className={twMerge("space-y-4", className)}>
        <div className="sticky top-0 z-10 bg-background-body">{renderNav("horizontal")}</div>
        {contentWithSpacing}
      </div>
    );
  }

  /*
   * For position="left" or "right", use CSS Grid layout.
   *
   * 🔴 `minmax(0, 1fr)`, never a bare `1fr`.
   *
   * A grid track's implicit minimum is `auto` — its content's own minimum
   * width — and `1fr` cannot shrink below that. So a content column holding
   * anything that refuses to shrink (a chart, a map, a wide table) sizes the
   * track to that content instead of to the space available, and the nav
   * column is pushed PAST the grid's right edge rather than the content
   * yielding to it.
   *
   * Measured on the device entity page before this change: a 1342px grid
   * resolved its columns to `1162px 240px` with a 2rem gap — 1434px of track
   * in a 1342px box, so the rail hung 92px outside its own container while
   * every container edge still measured correctly. `minmax(0, 1fr)` lets the
   * content track shrink, which is what makes the nav's fixed width
   * authoritative.
   */
  const gridTemplateColumns =
    position === "left" ? `var(--nav-width) minmax(0, 1fr)` : `minmax(0, 1fr) var(--nav-width)`;

  const navTopClass = navStickyTop ? "" : "top-20";
  const navStyle = navStickyTop ? ({ top: navStickyTop } as React.CSSProperties) : undefined;
  // Both rails left-align their links (matches the design's "On this page" rail and the base
  // SectionNav). The right rail previously forced `[&_a]:text-right`, which — combined with
  // SectionNav's `flex justify-between` items — left single-line labels at the left but pulled a
  // WRAPPED label (e.g. "Program enrollments") to the right edge, reading as a crooked indent.
  const navClasses = twMerge("hidden lg:block sticky self-start", navTopClass);

  return (
    <>
      {/* Mobile: horizontal nav above content */}
      <div className="lg:hidden sticky top-0 z-10 mt-3 mb-4 bg-background-body border-b border-border-default shadow-sm">
        {renderNav("horizontal", "py-3 mt-0")}
      </div>

      {/* Desktop: side-by-side layout with nav */}
      <div
        className={twMerge("lg:grid", className)}
        style={
          {
            gridTemplateColumns,
            gap,
            "--nav-width": navWidth,
          } as React.CSSProperties
        }
      >
        {position === "left" && (
          <nav className={navClasses} style={navStyle}>
            {renderNav("vertical")}
          </nav>
        )}

        {/* Content - consistent spacing applied */}
        {contentWithSpacing}

        {position === "right" && (
          <nav className={navClasses} style={navStyle}>
            {renderNav("vertical")}
          </nav>
        )}
      </div>
    </>
  );
}

// ——————————————————————————————————————————
// Scrollable Content
// ——————————————————————————————————————————

export type PageScrollableContentProps = {
  children: React.ReactNode;
  className?: string;
};

/**
 * ScrollableContent
 *
 * Helper component for creating scrollable content areas within PageLayout.
 * Commonly used with sticky headers and controls for data-heavy pages.
 *
 * Example usage:
 * ```tsx
 * <PageLayout className="flex flex-col h-screen">
 *   <PageLayout.Header sticky={true} />
 *   <DataControls sticky={true} stickyTop="120px" />
 *   <PageLayout.ScrollableContent>
 *     <DataTable data={data} columns={columns} />
 *   </PageLayout.ScrollableContent>
 * </PageLayout>
 * ```
 */
function ScrollableContent({ children, className }: PageScrollableContentProps) {
  return <div className={twMerge("flex-1 overflow-auto min-h-0", className)}>{children}</div>;
}

// ——————————————————————————————————————————
// Compound API
// ——————————————————————————————————————————

PageLayout.Header = Header;
PageLayout.Tabs = Tabs;
PageLayout.Content = Content;
PageLayout.Aside = Aside;
PageLayout.ScrollableContent = ScrollableContent;
